<script lang="ts">
	import Confetti from 'svelte-confetti';
	import { push } from 'svelte-spa-router';
	// Design system components
	import {
		IconButton,
		Text,
		Container,
		Button,
		Modal,
		Stack,
		CircleAvatar,
		Input,
		toast,
		Textarea,
		Collapse,
		Select,
		Checkbox,
		Icon,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { tick } from 'svelte';

	import TestConfetti from './TestConfetti.svelte';
	import ResetRate from './ResetRate.svelte';
	import SendLocalCommandsUtility from './SendLocalCommandsUtility.svelte';
	import DeviceRuntimeInspector from './DeviceRuntimeInspector.svelte';
	import ResetPPandGTC from './ResetPPandGTC.svelte';
	import ResetAlexaIntegrationPrompt from './ResetAlexaIntegrationPrompt.svelte';
	import ResetNewsletter from './ResetNewsletter.svelte';
	import ResetPremiumEventLogAds from './ResetPremiumEventLogAds.svelte';
	import ResetPremiumSceneConditionAds from './ResetPremiumSceneConditionAds.svelte';
	import ResetPremiumWeatherWidgetAds from './ResetPremiumWeatherWidgetAds.svelte';
	import ResetBlePromo from './ResetBlePromo.svelte';
	import VerivoxDevSection from './VerivoxDevSection.svelte';

	import { getLandingHref } from 'src/helpers/main_menu';
	import { loadingData } from 'src/stores/ui/loading-data';
	import { refreshDataCalls } from 'src/stores/features/dev/refresh-data-calls';
	import {
		crefresh_token,
		getReq,
		handleToken,
		postReq,
		pullDataWithLoading,
	} from 'src/services/http';
	import ResetWhatsNewFeatures from './ResetWhatsNewFeatures.svelte';
	import ResetHomesOnboarding from './ResetHomesOnboarding.svelte';
	import ResetGlobalTempUnit from './ResetGlobalTempUnit.svelte';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { app as NativeApp, os } from 'src/stores/session/native-bridge';
	import { NativeOperationSystem } from 'src/services/init/types/init-type';
	import { testDemoPromotionsModals } from 'src/stores/ui/promotions';
	import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
	import { delete_home, save_home } from 'src/helpers/home_helpers';
	import { delete_profile, save_profile } from 'src/helpers/profile_helpers';
	import { home_data, profile_data } from 'src/stores/account/user';
	import { get } from 'svelte/store';
	import { HttpClient } from 'src/services/utils/http-client';

	// Import DUI functions
	import { duiSave, duiSaveField, duiDelete, duiList } from 'src/services/php-api/device_ui';
	import { device_ui } from 'src/stores/features/device-ui';
	import type { DeviceUI, DeviceUIField } from 'src/types/DeviceUI';
	import {
		askForSecuritySettings,
		showBiometricAuthAndroid,
		load_asset_android,
	} from 'src/services/native_container_requests_android';
	import { biometry_store } from 'src/stores/features/biometry';
	import { showBiometricAuthIos, load_asset_ios } from 'src/services/native_container_requests_ios';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { isRepoUser } from 'src/helpers/user_server_conditions';
	import { isSubUnit } from 'src/helpers/user_helpers';
	import { sidepanelDesktopOnTop } from 'src/stores/ui/sidepanel-public';
	import { eventLogV2Enabled } from 'src/stores/event_log_v2_enabled';
	import {
		allEnergyWidgetsGalleryEnabled,
		setAllEnergyWidgetsGalleryEnabled,
	} from 'src/stores/features/dev/all-energy-widgets-gallery';
	import {
		allDeviceWidgetsGalleryEnabled,
		setAllDeviceWidgetsGalleryEnabled,
	} from 'src/stores/features/dev/all-device-widgets-gallery';
	import {
		energyWidgetVersionsEnabled,
		setEnergyWidgetVersionsEnabled,
	} from 'src/stores/features/dev/energy-widget-versions';
	import { ALL_ENERGY_WIDGETS_DASHBOARD_ROUTE } from 'src/components/EnergyMonitoring/v2/energy-v2-types';
	import { ALL_DEVICE_WIDGETS_DASHBOARD_ROUTE } from 'src/components/Dashboards/all-device-widgets';
	import { DEFAULT_DASH_ID } from 'src/components/Dashboards/dash-helpers';
	import {
		safeAreaDebugEnabled,
		setSafeAreaDebugEnabled,
	} from 'src/stores/features/dev/safe-area-debug';
	import {
		deviceTableViewEnabled,
		setDeviceTableViewEnabled,
	} from 'src/stores/features/dev/device-table-view';
	import {
		setWidgetTypeOutlinesEnabled,
		widgetTypeOutlinesEnabled,
	} from 'src/stores/features/dev/widget-type-outlines';
	import {
		underStatusBarEnabled,
		underNavigationBarEnabled,
		setUnderStatusBarEnabled,
		setUnderNavigationBarEnabled,
		setUnderSystemBarsEnabled,
	} from 'src/stores/features/dev/under-status-bar';
	import {
		navScrollBottomExpandEnabled,
		setNavScrollBottomExpandEnabled,
	} from 'src/stores/features/dev/nav-scroll-bottom-expand';
	import {
		navScrollCompactEnabled,
		setNavScrollCompactEnabled,
	} from 'src/stores/features/dev/nav-scroll-compact';
	import {
		navDockWideEnabled,
		navIndicatorCircleEnabled,
		setNavDockWideEnabled,
		setNavIndicatorCircleEnabled,
	} from 'src/stores/features/dev/nav-dock-style';
	import {
		navDockHorizontalSelectEnabled,
		setNavDockHorizontalSelectEnabled,
	} from 'src/stores/features/dev/nav-dock-horizontal-select';
	import {
		NAV_DOCK_FADE_HEIGHT_DEFAULT,
		NAV_DOCK_FADE_HEIGHT_MAX,
		NAV_DOCK_FADE_HEIGHT_MIN,
		navDockFadeHeightPx,
		setNavDockFadeHeightPx,
	} from 'src/stores/features/dev/nav-dock-fade';
	import {
		tabSwipeNavigateEnabled,
		setTabSwipeNavigateEnabled,
		tabSwipeAnimationEnabled,
		setTabSwipeAnimationEnabled,
		tabSwipeIosEdgeClearanceEnabled,
		setTabSwipeIosEdgeClearanceEnabled,
	} from 'src/stores/features/dev/tab-swipe-navigate';
	import {
		longPressEditEnabled,
		setLongPressEditEnabled,
	} from 'src/stores/features/dev/long-press-edit';
	import {
		forceListSkeletonEnabled,
		setForceListSkeletonEnabled,
	} from 'src/stores/features/dev/force-list-skeleton';
	import {
		HEADER_CHROME_PACKAGES,
		HEADER_CONTENT_FADE_HEIGHT_DEFAULT,
		HEADER_CONTENT_FADE_HEIGHT_MAX,
		HEADER_CONTENT_FADE_HEIGHT_MIN,
		getHeaderChromePackageConfig,
		headerChromePackage,
		headerContentFadeHeightPx,
		setHeaderChromePackage,
		setHeaderContentFadeHeightPx,
	} from 'src/stores/features/dev/header-chrome';
	import {
		headerScrollCollapseEnabled,
		setHeaderScrollCollapseEnabled,
	} from 'src/stores/features/dev/header-scroll-collapse';
	import appPackage from '../../../package.json';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import { deleteDeviceRecordings, deleteCameraStorageKey } from 'src/services/cam-cloud/recordings';
	import { clearStoredMasterKey } from 'src/components/Camera/helpers/encryption/master-key-management';
	import { camera_setup_prompt } from 'src/components/Camera/stores/camera_setup_prompt';
	import { camera_key_prompt } from 'src/components/Camera/stores/camera_key_prompt';

	export let openDevModal = false;

	/** Declared `@Shelly/shelly-design-system` version for this app build. */
	const designSystemPackage =
		(appPackage as { dependencies?: Record<string, string> }).dependencies?.[
			'@Shelly/shelly-design-system'
		]?.replace(/^[~^]/, '') ?? '—';

	const FE_ENV_MONITOR_URL = 'https://fe-env-monitor.shelly.link';

	let navDockFadeHeightInput = String($navDockFadeHeightPx);
	$: navDockFadeHeightInput = String($navDockFadeHeightPx);

	let headerContentFadeHeightInput = String($headerContentFadeHeightPx);
	$: headerContentFadeHeightInput = String($headerContentFadeHeightPx);

	$: headerChromeConfig = getHeaderChromePackageConfig($headerChromePackage);

	let testConfetti = false;
	let duiSectionOpen = false;
	let envMonitorSectionOpen = false;
	let isSubUnitSectionOpen = false;
	let deviceRuntimeSectionOpen = false;
	let energyPriceScheme: ColorSchemeKey = 'scheme1';
	let showDeleteCloudRecordingsSetupModal = false;
	let deleteCloudRecordingsSetupLoading = false;
	let selectedCameraMac: string | null = null;
	let deleteAllRecordings = true;
	//Expired token for JJ
	let expiredTokenJJ =
		'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJwd2QiLCJpYXQiOjE3NDY2MzgzNzcsInVzZXJfaWQiOiIxMTI4MTAyIiwic24iOiI1IiwidXNlcl9hcGlfdXJsIjoiaHR0cHM6XC9cL3JlcG8uc2hlbGx5LmNsb3VkIiwibiI6MTgxNSwiZXhwIjoxNzQ2NjM4Njc3fQ.vekcuk_f9a4pTZSrtvYP3kHVr0ypl-HpFAOoTGMhloI';
	//Expired refresh token for JJ
	// @ToDo: Update with test token for JJ
	let invalidRefreshTokenJJ =
		'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJpb3R1LzEiLCJzdWIiOiIzMjM2NDk0IiwiYXVkIjoicHdkIiwic24iOiIzIiwiaWF0IjoxNzUwMjc1MjU1LCJqdGkiOjk2MzQsImV4cCI6MTc1NTQ1OTI1NX0.AHl6wv_RiR_KxAxOQXrjmDChQIbCBSYeKnwNjSCAIr';
	let expiredRefreshTokenJJ =
		'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJpb3R1LzEiLCJzdWIiOiIxMTI4MTAyIiwiYXVkIjoicHdkIiwic24iOiI1IiwiaWF0IjoxNzUwMjc3MTA1LCJqdGkiOjE5OTEsImV4cCI6MTc1MDM2MzUwNX0.4SDj3941MBNP3Lu2_mvZcHXeFS_27ea4egLb9Poyf60';

	$: biometricAuthResponse = $biometry_store;

	$: customToken = $user_data.token;

	let loadAssetUrl = 'https://control.shelly.cloud/assets/en-312f84b7.js';

	const loadAsset = async () => {
		try {
			let blob = null;
			if ($os === 'IOS') {
				blob = await load_asset_ios(loadAssetUrl);
			} else {
				blob = await load_asset_android(loadAssetUrl);
			}

			// Convert Blob to text
			const text = await blob.text();

			// Try to parse as JSON, if it fails it's likely a JS file
			let data;
			let isJson = false;
			try {
				data = JSON.parse(text);
				isJson = true;
			} catch {
				// Not JSON, treat as text (e.g., JavaScript file)
				data = text;
			}

			// If it's JSON, check for error response
			if (isJson && data.isok === false) {
				const errorMessage = data.errors?.[Object.keys(data.errors || {})[0]] || 'Unknown error';
				console.error('Asset load error:', data);
				throw new Error(errorMessage);
			}

			toast.open({
				type: 'success',
				title: 'Asset loaded successfully',
			});
			console.log('Asset loaded data:', data);
		} catch (error) {
			toast.open({
				type: 'danger',
				title: 'Error loading asset',
				text: error?.message || '',
			});
			console.error('Error loading asset:', error);
		}
	};

	//#region Device UI Data Variables
	// Device UI Data variables
	let deviceId = '';
	let duiKey = '';
	let duiFieldData = '';
	let duiDeviceData = '';

	// DUI function handlers

	const handleDuiSaveField = async () => {
		try {
			const result = await duiSaveField(
				deviceId,
				duiKey as DeviceUIField,
				duiFieldData as unknown as DeviceUI[DeviceUIField],
			);
			console.log('DUI Update Field result:', result);

			if (!result.isok) {
				throw new Error(
					result?.errors?.[Object.keys(result?.errors || {})[0]] || 'Unknown error',
				);
			}

			toast.open({
				type: 'success',
				title: 'DUI field updated successfully',
			});
		} catch (error) {
			console.error('DUI Update Field error:', error);
			toast.open({
				type: 'danger',
				title: 'Error updating DUI field',
				text: error?.message || '',
			});
		}
	};

	const handleDuiSave = async () => {
		try {
			const data = JSON.parse(duiDeviceData);
			const result = await duiSave(deviceId, data);
			console.log('DUI Save result:', result);

			if (!result.isok) {
				throw new Error(
					result?.errors?.[Object.keys(result?.errors || {})[0]] || 'Unknown error',
				);
			}
			toast.open({
				type: 'success',
				title: 'DUI data saved successfully',
			});
		} catch (error) {
			console.error('DUI Save error:', error);
			toast.open({
				type: 'danger',
				title: 'Error saving DUI data',
				text: error?.message || '',
			});
		}
	};

	const handleDuiDelete = async () => {
		try {
			const result = await duiDelete(deviceId);
			console.log('DUI Delete result:', result);
			if (!result.isok) {
				throw new Error(
					result?.errors?.[Object.keys(result?.errors || {})[0]] || 'Unknown error',
				);
			}
			toast.open({
				type: 'success',
				title: 'DUI data deleted successfully',
			});
		} catch (error) {
			console.error('DUI Delete error:', error);
			toast.open({
				type: 'danger',
				title: 'Error deleting DUI data',
				text: error?.message || '',
			});
		}
	};

	const handleDuiList = async () => {
		try {
			const result = await duiList();
			console.log('DUI List result:', result);
			if (!result.isok) {
				throw new Error(
					result?.errors?.[Object.keys(result?.errors || {})[0]] || 'Unknown error',
				);
			}
			toast.open({
				type: 'success',
				title: 'DUI list retrieved successfully',
			});
		} catch (error) {
			console.error('DUI List error:', error);
			toast.open({
				type: 'danger',
				title: 'Error retrieving DUI list',
			});
		}
	};
	//#endregion

	const handlePullDown = (e) => {
		if (!$loadingData) {
			const timeStamp = Date.now();
			refreshDataCalls.customUpdate(timeStamp);
			pullDataWithLoading(); // calls => handleInitialLoad
		}
	};

	const resetPremiumData = async () => {
		await getReq('https://test-paypal.shelly.cloud/api/reset-subscription', {})
			.then(() => {
				alert('Premium data reset');
			})
			.catch(() => {
				alert('Error resetting premium data');
			});
	};

	const deleteVoucherFromAccount = async () => {
		try {
			let token = localStorage.getItem('user_data')
				? JSON.parse(localStorage.getItem('user_data')).token
				: null;
			let infraInstance = new HttpClient(
				isRepoUser()
					? import.meta.env.VITE_REPO_VOUCHER_URL_TEST
					: import.meta.env.VITE_VOUCHER_URL_TEST,
			);
			infraInstance.setAuthToken(token);
			let request = await infraInstance.delete('/coupons/active_coupons');
			console.log('deleteVoucherFromAccount', request);
			if (request.status === 200) {
				toast.open({
					type: 'success',
					title: 'Voucher deleted successfully',
				});
			}
		} catch (error) {
			toast.open({
				type: 'error',
				title: 'Error deleting voucher from account',
			});
			console.error('Error deleting voucher from account:', error);
		}
	};
	const testThrowError = () => {
		throw new Error('Test runtime error');
	};
	const testRuntimeError = (e) => {
		e.testRuntimeError.length;
	};
	const testCaughtError = (e) => {
		try {
			e.testCaughtError.length;
		} catch (error) {
			console.log('Caught test error', error);
		}
	};
	const testConsoleError = (e) => {
		console.error('Console.error: test error', e);
	};

	const sendBiometricsRequest = async () => {
		const platform = get(os);

		if (platform === 'IOS') {
			const respone = await showBiometricAuthIos(
				'This is biometry',
				'Do you agree to pay me 43 BTC?', // description
				'I agree but I burnt my finger ( 🤫 this is cancel text 🤫)', // cancel
			);
			console.log('Biometric auth response:', respone);
			return;
		}

		const respone = await showBiometricAuthAndroid(
			'This is biometry',
			'Chakarakachooo',
			'Do you agree to pay me 43 BTC?',
			'I agree but I burnt my finger ( 🤫 this is cancel text 🤫)',
		);
		console.log('Biometric auth response:', respone);
	};

	const clearPinUnlockedDevices = async () => {
		const ds = get(devices);

		for (const [id, device] of Object.entries(ds)) {
			pin_unlocked_devices.remove_device(device);
		}
	};

	const askForSecurity = async () => {
		const respone = await askForSecuritySettings();
		console.log('Ask for security:', respone);
	};

	const resetNewLayoutOnboarding = async () => {
		profile_data.set({ ...get(profile_data), newLayoutOnboardingSeen: { seen: false } });
		save_profile('newLayoutOnboardingSeen', { seen: false });

		openDevModal = false;
		// Let the dev modal unmount before opening the onboarding modal.
		await tick();
		showNewLayoutOnboarding.set(true);
	};

	const toggleSidepanelDesktopOnTop = (bool: boolean = true) => {
		sidepanelDesktopOnTop.set(bool);
		return;
	};

	const setEventLogV2Enabled = (enabled: boolean) => {
		eventLogV2Enabled.set(enabled);
		toast.open({
			type: 'info',
			title: `Event log ${enabled ? 'V2' : 'V1'} enabled`,
			text: 'Reload Activity log to apply.',
		});
	};

	/**
	 *  tmpk_valid is stored as a 'YYYY-MM-DD HH:MM:SS' string (see GrantAccess.svelte).
	 *	Set it to an old date so support access reads as expired.
	 */
	const setExpiredSupportAccess = () => {
		const oldDate = new Date('2000-01-01T00:00:00');
		const pad = (n: number) => `${n}`.padStart(2, '0');
		const tmpkValid =
			`${oldDate.getFullYear()}-${pad(oldDate.getMonth() + 1)}-${pad(oldDate.getDate())} ` +
			`${pad(oldDate.getHours())}:${pad(oldDate.getMinutes())}:${pad(oldDate.getSeconds())}`;

		user_data.set({
			...get(user_data),
			tmpk_valid: tmpkValid,
		});

		console.log('Set expired tmpk_valid', tmpkValid);
		toast.open({
			type: 'success',
			title: 'Set expired support access (tmpk_valid)',
			text: tmpkValid,
		});
	};

	/** Camera devices offered in the setup-deletion dropdown, keyed by normalised MAC. */
	$: cameraOptions = getCameraDevices($devices).map((camera) => ({
		title: camera.name || camera.id,
		value: real_id(camera.id),
	}));

	$: selectedCameraOption = cameraOptions.find((option) => option.value === selectedCameraMac) || null;

	$: selectedCameraTitle = selectedCameraOption?.title || selectedCameraMac || '';

	const handleCameraSelect = (event: CustomEvent) => {
		const value = event.detail?.value;
		selectedCameraMac = typeof value === 'string' ? value : null;
	};

	/**
	 * Renders an `HttpClient` rejection as a readable line. Failed requests reject
	 * with the plain `{ status, error }` object from `ApiError.handleFetchError`
	 * rather than an `Error`, so `message` alone is not enough.
	 */
	const describeRequestError = (error: unknown): string => {
		const err = error as { status?: number; error?: string; message?: string };
		const detail = (err?.error || err?.message || '').replace(/^Error:\s*/, '');
		if (detail) return detail;
		return err?.status ? `Request failed with code ${err.status}` : 'Request failed';
	};

	/**
	 * Wipes one camera's Cloud Recordings setup: its cloud-backed encryption key
	 * and every local copy of that key (encrypted backup, decrypted master key and
	 * derived password key, across all rotation eras) plus the once-per-session
	 * memory of the setup and unlock prompts, so both auto-open again for that
	 * camera without a reload. Its cloud recordings go too
	 * unless `deleteAllRecordings` is unchecked. Each cloud call is independent so
	 * one failure does not abort the rest, and the local wipe always runs — but any
	 * failed request is reported instead of being swallowed behind a success toast.
	 */
	const deleteCloudRecordingsSetup = async () => {
		const mac = selectedCameraMac;
		if (!mac) return;

		deleteCloudRecordingsSetupLoading = true;
		const failures: string[] = [];

		try {
			if (deleteAllRecordings) {
				try {
					await deleteDeviceRecordings(mac);
				} catch (error) {
					console.error('Failed to delete cloud recordings for', mac, error);
					failures.push(`Cloud Recordings: ${describeRequestError(error)}`);
				}
			}

			try {
				await deleteCameraStorageKey(mac);
			} catch (error) {
				console.error('Failed to delete cloud encryption key for', mac, error);
				failures.push(`Encryption Key: ${describeRequestError(error)}`);
			}

			clearStoredMasterKey(mac);
			camera_setup_prompt.resetDevice(mac);
			camera_key_prompt.resetDevice(mac);

			if (failures.length) {
				toast.open({
					type: 'danger',
					title: `Cloud reset incomplete for ${selectedCameraTitle}`,
					text: `${failures.join(' | ')} — local keys were cleared, cloud data may remain.`,
					duration: 10000,
				});
				return;
			}

			toast.open({
				type: 'success',
				title: 'Cloud Recordings setup reset',
				text: `${selectedCameraTitle} (${mac})`,
			});
		} catch (error) {
			console.error('Failed to reset Cloud Recordings setup', error);
			toast.open({
				type: 'danger',
				title: 'Error resetting Cloud Recordings setup',
				text: describeRequestError(error),
				duration: 10000,
			});
		} finally {
			deleteCloudRecordingsSetupLoading = false;
			showDeleteCloudRecordingsSetupModal = false;
		}
	};
</script>

<Modal
	showModal={openDevModal}
	on:close={() => {
		openDevModal = false;
	}}
	title={'Dev Utilities'}
	backdropClose={false}
>
	<Stack slot="content" gap="m" class="d-flex flex-direction-column">
		<div class="d-flex width-full align-items-center gap-s">
			<CircleAvatar size="l" class="mb-m mr-m" image={'./images/dev/arny.jpeg'} />
			<Button
				class="width-full"
				icon="fas fa-sign-out-alt"
				variant="danger"
				on:click={() => {
					localStorage.clear();
					window.location.reload();
				}}
			>
				Total Logout!
			</Button>
		</div>

		<Text styleType="body-small" class="text-color-muted" title={designSystemPackage}>
			DS version: {designSystemPackage}
		</Text>

		<hr class="mb-s" />

		<Collapse
			id="device-runtime-inspector-section"
			title="Device Runtime Inspector"
			expanded={deviceRuntimeSectionOpen}
			on:click={() => (deviceRuntimeSectionOpen = !deviceRuntimeSectionOpen)}
		>
			<DeviceRuntimeInspector onRefresh={() => handlePullDown(undefined)} />
		</Collapse>

		<hr class="mb-s" />

		<Collapse
			id="env-monitor-section"
			title="Sub Unit info"
			expanded={isSubUnitSectionOpen}
			on:click={() => (isSubUnitSectionOpen = !isSubUnitSectionOpen)}
		>
			<Text>is Sub: {isSubUnit()}</Text>
			<Text>is Sub return value: {get(user_settings)?.sub}</Text>
			<Text>Unit info</Text>
			{#each Object.entries(get(user_data)?.own_units || {}) as [id, data] (id)}
				<Text>{id}: {data.name}</Text>
			{/each}
			<Text>Sub units</Text>
			{#each Object.entries(get(user_data)?.sub_units || {}) as [id, data] (id)}
				<Text>{id}: {data.name}</Text>
			{/each}
		</Collapse>

		<hr class="mb-s" />

		<Collapse
			id="env-monitor-section"
			title="FE Env Monitor"
			expanded={envMonitorSectionOpen}
			on:click={() => (envMonitorSectionOpen = !envMonitorSectionOpen)}
		>
			<Stack gap="s" class="mb-l">
				<Stack gap="s" class="d-flex align-items-center">
					<Text styleType="body-small" class="mb-0"
						>Dev environment status. Open in new tab if embedding is blocked.</Text
					>
					<Button
						variant="secondary"
						size="s"
						on:click={() => window.open(FE_ENV_MONITOR_URL, '_blank', 'noopener,noreferrer')}
					>
						Open in new tab
					</Button>
				</Stack>
				<Container padding="0">
					<iframe
						src={FE_ENV_MONITOR_URL}
						title="FE Environment Monitor"
						class="env-monitor-iframe width-full"
						loading="lazy"
					/>
				</Container>
			</Stack>
		</Collapse>

		<hr class="mb-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-s">Reinitialize</Text>
			<Stack gap="m" class="d-flex align-items-center ">
				<Button
					class="width-md-xl width-sm-full"
					icon="fas fa-arrows-rotate"
					on:click={() => window.location.reload()}
				>
					Reload App
				</Button>
				<Button
					variant="secondary"
					class="width-md-xl width-sm-full"
					icon="fas fa-download"
					on:click={(e) => handlePullDown(e)}
				>
					Refresh Data</Button
				>
			</Stack>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset New Layout Onboarding</Text>
			<Text class="mb-s"
				>Shows the new layout onboarding modal again. (resets $profile_data ->
				`newLayoutOnboardingSeen`)</Text
			>
			<Button variant="secondary" on:click={() => resetNewLayoutOnboarding()}>
				Reset & Show Onboarding
			</Button>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m">Activity log</Text>
			<Text styleType="body-small" class="mb-m">
				Switch between event log V1 (positional payloads) and V2 (JSON payloads). Setting is
				saved locally.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$eventLogV2Enabled ? 'primary' : 'secondary'}
					icon="fas fa-list-timeline"
					on:click={() => setEventLogV2Enabled(true)}
				>
					Event Log V2
				</Button>
				<Button
					variant={!$eventLogV2Enabled ? 'primary' : 'secondary'}
					icon="fas fa-list"
					on:click={() => setEventLogV2Enabled(false)}
				>
					Event Log V1
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current mode: <b>{$eventLogV2Enabled ? 'V2' : 'V1'}</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Safe area debug</Text>
			<Text styleType="body-small" class="mb-m">
				Shows a red top ruler sized to <code>env(safe-area-inset-top)</code>, a matching blue
				bottom ruler for <code>env(safe-area-inset-bottom)</code>, and a dismissible metrics
				panel (reappears when you disable then re-enable). Use it to see whether a large gap is
				CSS safe-area, stacked padding, or native WebView inset. Preference is saved locally.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$safeAreaDebugEnabled ? 'primary' : 'secondary'}
					icon="fas fa-ruler-combined"
					on:click={() => setSafeAreaDebugEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$safeAreaDebugEnabled ? 'primary' : 'secondary'}
					icon="fas fa-eye-slash"
					on:click={() => setSafeAreaDebugEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$safeAreaDebugEnabled ? 'On' : 'Off'}</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Dashboard widget debug</Text>
			<Text styleType="body-small" class="mb-m">
				Outline each newest dashboard widget type with a distinct color. A legend stays on screen
				while this is on.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$widgetTypeOutlinesEnabled ? 'primary' : 'secondary'}
					icon="fas fa-border-all"
					on:click={() => setWidgetTypeOutlinesEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$widgetTypeOutlinesEnabled ? 'primary' : 'secondary'}
					icon="fas fa-eye-slash"
					on:click={() => setWidgetTypeOutlinesEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$widgetTypeOutlinesEnabled ? 'On' : 'Off'}</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Android under system bars</Text>
			<Text styleType="body-small" class="mb-m">
				Calls
				<code>shelly://under_system_bars/?underStatusBar=…&amp;underNavigationBar=…</code>
				on the Android native shell. Each bar can be toggled independently. Defaults are on (edge-to-edge)
				and applied when the app mounts. Preferences are saved locally.
			</Text>

			<Text styleType="body-small" class="mb-s text-bold">Status bar</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$underStatusBarEnabled ? 'primary' : 'secondary'}
					icon="fas fa-mobile-screen"
					disabled={$os === 'IOS'}
					on:click={() => setUnderStatusBarEnabled(true)}
				>
					Under
				</Button>
				<Button
					variant={!$underStatusBarEnabled ? 'primary' : 'secondary'}
					icon="fas fa-mobile"
					disabled={$os === 'IOS'}
					on:click={() => setUnderStatusBarEnabled(false)}
				>
					Inset
				</Button>
			</Stack>

			<Text styleType="body-small" class="mb-s text-bold">Navigation bar</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$underNavigationBarEnabled ? 'primary' : 'secondary'}
					icon="fas fa-mobile-screen-button"
					disabled={$os === 'IOS'}
					on:click={() => setUnderNavigationBarEnabled(true)}
				>
					Under
				</Button>
				<Button
					variant={!$underNavigationBarEnabled ? 'primary' : 'secondary'}
					icon="fas fa-mobile"
					disabled={$os === 'IOS'}
					on:click={() => setUnderNavigationBarEnabled(false)}
				>
					Inset
				</Button>
			</Stack>

			<Text styleType="body-small" class="mb-s text-bold">Both</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$underStatusBarEnabled && $underNavigationBarEnabled
						? 'primary'
						: 'secondary'}
					disabled={$os === 'IOS'}
					on:click={() => setUnderSystemBarsEnabled(true, true)}
				>
					Both under
				</Button>
				<Button
					variant={!$underStatusBarEnabled && !$underNavigationBarEnabled
						? 'primary'
						: 'secondary'}
					disabled={$os === 'IOS'}
					on:click={() => setUnderSystemBarsEnabled(false, false)}
				>
					Both inset
				</Button>
			</Stack>

			<Text styleType="body-small" class="mt-s">
				Current: status <b>{$underStatusBarEnabled ? 'under' : 'inset'}</b>
				· nav <b>{$underNavigationBarEnabled ? 'under' : 'inset'}</b>
				{#if $os === 'IOS'}
					· <i>Android only</i>
				{/if}
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<!-- ── Navigation header ───────────────────────────────────────── -->
		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Header chrome</Text>
			<Text styleType="body-small" class="mb-m">
				New-layout Header + Tabs packages from the design system (glass uses a sliding dock-like
				selector). Default is Glass. Preference is saved locally.
			</Text>
			<Stack gap="s" class="d-flex align-items-center mb-m flex-wrap">
				{#each HEADER_CHROME_PACKAGES as pkg}
					<Button
						size="s"
						variant={$headerChromePackage === pkg.id ? 'primary' : 'secondary'}
						on:click={() => setHeaderChromePackage(pkg.id)}
					>
						{pkg.label}
					</Button>
				{/each}
			</Stack>
			<Text styleType="body-small" class="mb-m">
				Current: <b>{headerChromeConfig.label}</b> — {headerChromeConfig.blurb}
				· chromeStyle=<code>{headerChromeConfig.chromeStyle}</code>
				· tabsStyle=<code>{headerChromeConfig.tabsStyle}</code>
			</Text>
			<Text styleType="h3" class="mb-s text-bold">Top content fade</Text>
			<Text styleType="body-small" class="mb-s">
				Dissolve height (px) under the tab rail. Default
				{HEADER_CONTENT_FADE_HEIGHT_DEFAULT}.
			</Text>
			<Stack gap="m" class="d-flex align-items-end mb-m flex-wrap">
				<Input
					size="m"
					type="number"
					id="header-content-fade-height-input"
					label="Top fade height"
					min={HEADER_CONTENT_FADE_HEIGHT_MIN}
					max={HEADER_CONTENT_FADE_HEIGHT_MAX}
					bind:value={headerContentFadeHeightInput}
					class="width-md-m width-sm-full"
					on:change={() => {
						const next = Number(headerContentFadeHeightInput);
						if (Number.isFinite(next)) setHeaderContentFadeHeightPx(next);
					}}
				/>
				<Button
					variant="secondary"
					on:click={() => setHeaderContentFadeHeightPx(HEADER_CONTENT_FADE_HEIGHT_DEFAULT)}
				>
					Reset
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$headerContentFadeHeightPx}px</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Header scroll collapse</Text>
			<Text styleType="body-small" class="mb-m">
				On mobile, hide the header chrome (home name, title, profile, notifications, +) while
				scrolling down. Tabs stay put. Restores only after a sustained upward scroll (not at list
				bottom / overscroll). Preference is saved locally. Default is off.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$headerScrollCollapseEnabled ? 'primary' : 'secondary'}
					icon="fas fa-arrow-up"
					on:click={() => setHeaderScrollCollapseEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$headerScrollCollapseEnabled ? 'primary' : 'secondary'}
					icon="fas fa-arrow-down"
					on:click={() => setHeaderScrollCollapseEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$headerScrollCollapseEnabled ? 'On' : 'Off'}</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Long-press edit</Text>
			<Text styleType="body-small" class="mb-m">
				Touch and hold a widget or list card to rearrange. Default on. Kebab “Edit widgets” /
				“Reorder items” stays as the visible path. Mouse is ignored — desktop keeps the kebab.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$longPressEditEnabled ? 'primary' : 'secondary'}
					icon="fas fa-hand-point-up"
					on:click={() => setLongPressEditEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$longPressEditEnabled ? 'primary' : 'secondary'}
					icon="fas fa-ban"
					on:click={() => setLongPressEditEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$longPressEditEnabled ? 'On' : 'Off'}</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Force list skeletons</Text>
			<Text styleType="body-small" class="mb-m">
				Keep Home lists and dashboards (including the all-device widgets gallery) on skeleton
				screens so you can preview loading chrome. Local-only; off in production.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$forceListSkeletonEnabled ? 'primary' : 'secondary'}
					icon="fas fa-spinner"
					on:click={() => setForceListSkeletonEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$forceListSkeletonEnabled ? 'primary' : 'secondary'}
					icon="fas fa-ban"
					on:click={() => setForceListSkeletonEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$forceListSkeletonEnabled ? 'On' : 'Off'}</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Tab swipe navigate</Text>
			<Text styleType="body-small" class="mb-m">
				On Dash and Home (mobile/tablet), swipe left/right to change tabs. Ignores horizontal
				widgets, sliders, charts, and overflow scrollers. Does not wrap from the first tab to the
				last, or last to first. Energy and Activity Log main-menu sections are not included.
				Preference is saved locally. Default is on.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$tabSwipeNavigateEnabled ? 'primary' : 'secondary'}
					icon="fas fa-hand-point-left"
					on:click={() => setTabSwipeNavigateEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$tabSwipeNavigateEnabled ? 'primary' : 'secondary'}
					icon="fas fa-ban"
					on:click={() => setTabSwipeNavigateEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s mb-m">
				Current: <b>{$tabSwipeNavigateEnabled ? 'On' : 'Off'}</b>
			</Text>

			{#if $tabSwipeNavigateEnabled}
				<Text styleType="h3" class="mb-s text-bold">Tab change animation</Text>
				<Text styleType="body-small" class="mb-m">
					Option A: short directional fade/slide when Dash / Home tabs change by swipe. Taps do
					not animate. Default on.
				</Text>
				<Stack gap="m" class="d-flex align-items-center mb-m">
					<Button
						variant={$tabSwipeAnimationEnabled ? 'primary' : 'secondary'}
						on:click={() => setTabSwipeAnimationEnabled(true)}
					>
						Enable
					</Button>
					<Button
						variant={!$tabSwipeAnimationEnabled ? 'primary' : 'secondary'}
						on:click={() => setTabSwipeAnimationEnabled(false)}
					>
						Disable
					</Button>
				</Stack>
				<Text styleType="body-small" class="mt-s mb-m">
					Current: <b>{$tabSwipeAnimationEnabled ? 'On' : 'Off'}</b>
				</Text>

				<Text styleType="h3" class="mb-s text-bold">iOS edge clearance</Text>
				<Text styleType="body-small" class="mb-m">
					On iOS Dash / Home: disable WKWebView back/forward edge gestures, and ignore
					tab-swipes that start in the outer 28px. Avoids fighting system history navigation.
					Default off.
				</Text>
				<Stack gap="m" class="d-flex align-items-center mb-m">
					<Button
						variant={$tabSwipeIosEdgeClearanceEnabled ? 'primary' : 'secondary'}
						on:click={() => setTabSwipeIosEdgeClearanceEnabled(true)}
					>
						Enable
					</Button>
					<Button
						variant={!$tabSwipeIosEdgeClearanceEnabled ? 'primary' : 'secondary'}
						on:click={() => setTabSwipeIosEdgeClearanceEnabled(false)}
					>
						Disable
					</Button>
				</Stack>
				<Text styleType="body-small" class="mt-s">
					Current: <b>{$tabSwipeIosEdgeClearanceEnabled ? 'On' : 'Off'}</b>
				</Text>
			{/if}
		</Container>

		<hr class="mb-s mt-s" />

		<!-- ── Navigation dock ─────────────────────────────────────────── -->
		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Nav scroll compact</Text>
			<Text styleType="body-small" class="mb-m">
				When enabled, the floating dock shrinks on downward scroll and grows again on upward
				scroll / bottom expand / tap. When disabled, the dock stays large. Preference is saved
				locally. Default is off (always large).
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$navScrollCompactEnabled ? 'primary' : 'secondary'}
					icon="fas fa-compress"
					on:click={() => setNavScrollCompactEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$navScrollCompactEnabled ? 'primary' : 'secondary'}
					icon="fas fa-expand"
					on:click={() => setNavScrollCompactEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s mb-m">
				Current: <b>{$navScrollCompactEnabled ? 'On' : 'Off'}</b>
			</Text>

			{#if $navScrollCompactEnabled}
				<Text styleType="h3" class="mb-s text-bold">Nav scroll bottom expand</Text>
				<Text styleType="body-small" class="mb-m">
					When the floating pill is compact from scrolling down, restore it once the scroller
					reaches the bottom. Needed on Android WebViews that do not fire overscroll deltas
					(iOS rubber-band usually expands without this). Preference is saved locally. Default
					is on.
				</Text>
				<Stack gap="m" class="d-flex align-items-center mb-m">
					<Button
						variant={$navScrollBottomExpandEnabled ? 'primary' : 'secondary'}
						icon="fas fa-expand"
						on:click={() => setNavScrollBottomExpandEnabled(true)}
					>
						Enable
					</Button>
					<Button
						variant={!$navScrollBottomExpandEnabled ? 'primary' : 'secondary'}
						icon="fas fa-compress"
						on:click={() => setNavScrollBottomExpandEnabled(false)}
					>
						Disable
					</Button>
				</Stack>
				<Text styleType="body-small" class="mt-s">
					Current: <b>{$navScrollBottomExpandEnabled ? 'On' : 'Off'}</b>
				</Text>
			{/if}
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">PillShapedDock</Text>
			<Text styleType="body-small" class="mb-m">
				Floating capsule dock visuals and experiments. Preferences are saved locally.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m flex-wrap">
				<Button
					variant={$navIndicatorCircleEnabled ? 'primary' : 'secondary'}
					icon="fas fa-circle"
					on:click={() => setNavIndicatorCircleEnabled(!$navIndicatorCircleEnabled)}
				>
					Circle indicator {$navIndicatorCircleEnabled ? 'on' : 'off'}
				</Button>
				<Button
					variant={$navDockWideEnabled ? 'primary' : 'secondary'}
					icon="fas fa-arrows-left-right"
					on:click={() => setNavDockWideEnabled(!$navDockWideEnabled)}
				>
					Wide dock {$navDockWideEnabled ? 'on' : 'off'}
				</Button>
			</Stack>

			<Text styleType="h3" class="mb-s text-bold">Horizontal select</Text>
			<Text styleType="body-small" class="mb-m">
				When enabled, drag anywhere on the dock bar to scrub the selector; release snaps to the
				nearest item. Horizontal wheel / trackpad also nudges selection (finger right → selector
				right). Tap still works. Most useful on the horizontal tablet/mobile capsule. Default
				off.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$navDockHorizontalSelectEnabled ? 'primary' : 'secondary'}
					icon="fas fa-exchange-alt"
					on:click={() => setNavDockHorizontalSelectEnabled(true)}
				>
					Enable
				</Button>
				<Button
					variant={!$navDockHorizontalSelectEnabled ? 'primary' : 'secondary'}
					on:click={() => setNavDockHorizontalSelectEnabled(false)}
				>
					Disable
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s mb-m">
				Current: <b>{$navDockHorizontalSelectEnabled ? 'On' : 'Off'}</b>
			</Text>

			<Text styleType="h3" class="mb-s text-bold">Dock scroll fade</Text>
			<Text styleType="body-small" class="mb-s">
				Scroll fade height (px), measured upward from the dock floor. Default
				{NAV_DOCK_FADE_HEIGHT_DEFAULT}.
			</Text>
			<Stack gap="m" class="d-flex align-items-end mb-m flex-wrap">
				<Input
					size="m"
					type="number"
					id="nav-dock-fade-height-input"
					label="Fade height"
					min={NAV_DOCK_FADE_HEIGHT_MIN}
					max={NAV_DOCK_FADE_HEIGHT_MAX}
					bind:value={navDockFadeHeightInput}
					class="width-md-m width-sm-full"
					on:change={() => {
						const next = Number(navDockFadeHeightInput);
						if (Number.isFinite(next)) setNavDockFadeHeightPx(next);
					}}
				/>
				<Button
					variant="secondary"
					on:click={() => setNavDockFadeHeightPx(NAV_DOCK_FADE_HEIGHT_DEFAULT)}
				>
					Reset
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$navDockFadeHeightPx}px</b>
			</Text>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m">Sidepanel behavior</Text>
			<Text styleType="body-small" class="mb-m">
				Controls how the sidepanel behaves on desktop screens.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$sidepanelDesktopOnTop ? 'primary' : 'secondary'}
					icon="fas fa-layer-group"
					on:click={() => toggleSidepanelDesktopOnTop(true)}
				>
					Always on Top
				</Button>
				<Button
					variant={!$sidepanelDesktopOnTop ? 'primary' : 'secondary'}
					icon="fas fa-sidebar-flip"
					on:click={() => toggleSidepanelDesktopOnTop(false)}
				>
					Hide / Show
				</Button>
			</Stack>

			<Text styleType="body-small" class="mt-s">
				<b>Always on Top</b> — sidepanel is pinned and always visible alongside content.
			</Text>
			<Text styleType="body-small" class="mt-s"
				><b>Hide/Show</b> — sidepanel can be collapsed and toggled by the user.</Text
			>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m">Mobile: Dynamic load assets</Text>

			<Input
				size="m"
				type="text"
				id="load-asset-url-input"
				aria-labelledby="load-asset-url-input"
				label="Load Asset URL"
				placeholder="Enter load asset URL"
				bind:value={loadAssetUrl}
				class="width-md-xl width-sm-full"
			/>

			<Button variant="secondary" on:click={loadAsset} class="width-md-xl width-sm-full">
				Load Asset
			</Button>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m">Device UI Data</Text>
			<Collapse
				id="dui-data-requests"
				title="DUI Data Requests"
				expanded={duiSectionOpen}
				on:click={() => (duiSectionOpen = !duiSectionOpen)}
				class="mb-m"
			>
				<Stack gap="s" class="mb-l">
					<Button
						variant="secondary"
						on:click={handleDuiList}
						class="width-md-xl width-sm-full"
					>
						DUI List
					</Button>
					<hr class="mb-m mt-m" />

					<Text styleType="h2" class="mb-m">Single field</Text>

					<Input
						size="m"
						type="text"
						id="device-id-input"
						aria-labelledby="device-id-input"
						label="Device ID"
						placeholder="Enter device ID"
						bind:value={deviceId}
						class="width-md-xl width-sm-full"
					/>

					<Input
						size="m"
						type="text"
						id="dui-key-input"
						aria-labelledby="dui-key-input"
						label="DUI Key"
						placeholder="Enter DUI key"
						bind:value={duiKey}
						class="width-md-xl width-sm-full"
					/>

					<Textarea
						size="m"
						id="dui-data-input"
						aria-labelledby="dui-data-input"
						label="DUI Field Value (JSON)"
						placeholder="Enter DUI data as JSON"
						bind:value={duiFieldData}
						class="width-md-xl width-sm-full"
					/>

					<Stack gap="m" class="d-flex align-items-center">
						<Button
							variant="primary"
							class="width-md-xl width-sm-full"
							on:click={handleDuiSaveField}
							disabled={!deviceId || !duiKey || !duiFieldData}
						>
							DUI Update Field
						</Button>
						<Button
							variant="secondary"
							on:click={() =>
								(duiFieldData = device_ui.getDeviceUIField(deviceId, duiKey))}
							disabled={!deviceId || !duiKey}
						>
							Get (Store)
						</Button>
					</Stack>
					<hr class="mb-m mt-m" />
					<Text styleType="h2" class="mb-m">Device Data</Text>

					<Input
						size="m"
						type="text"
						id="device-id-input"
						aria-labelledby="device-id-input"
						label="Device ID"
						placeholder="Enter device ID"
						bind:value={deviceId}
						class="width-md-xl width-sm-full"
					/>

					<Textarea
						size="m"
						id="dui-data-input"
						aria-labelledby="dui-data-input"
						label="DUI Data (JSON)"
						placeholder="Enter DUI data as JSON"
						bind:value={duiDeviceData}
						class="width-md-xl width-sm-full"
					/>
					<Stack gap="m" class="d-flex align-items-center">
						<Button
							variant="primary"
							on:click={handleDuiSave}
							class="width-md-xl width-sm-full"
							disabled={!deviceId || !duiDeviceData}
						>
							DUI Save Device
						</Button>
						<Button
							variant="secondary"
							on:click={() =>
								(duiDeviceData = JSON.stringify(device_ui.getDeviceUI(deviceId)))}
							disabled={!deviceId}
						>
							Get (Store)
						</Button>
					</Stack>
					<Button
						variant="danger"
						on:click={handleDuiDelete}
						class="width-md-xl width-sm-full"
						disabled={!deviceId}
					>
						DUI Delete Device
					</Button>
				</Stack>
			</Collapse>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Authentication Data</Text>
			<Button
				on:click={() => {
					user_data.set({
						...get(user_data),
						password: 'wrong',
					});
				}}>Set Wrong Password</Button
			>

			<Button
				variant="secondary"
				class="ml-l"
				on:click={setExpiredSupportAccess}
				aria-label="Set expired support access date to tmpk_valid"
			>
				Set Expired Support Access
			</Button>

			<hr class="mt-l mb-l" />
			<Text class="mb-m text-bold">Refresh Token</Text>
			<Stack gap="m" class="d-flex align-items-center flex-direction-column">
				<Button
					variant="primary"
					class="width-full"
					on:click={() => {
						user_data.set({
							...get(user_data),
							refresh_token: expiredRefreshTokenJJ,
						});
						console.log('Setting Expired JJ Refresh Token', expiredRefreshTokenJJ);
					}}>Set Expired</Button
				>
				<Button
					variant="secondary"
					class="width-full"
					on:click={() => {
						user_data.set({
							...get(user_data),
							refresh_token: invalidRefreshTokenJJ,
						});
						console.log('Setting Invalid JJ Refresh Token', invalidRefreshTokenJJ);
					}}>Set Invalid</Button
				>
				<Button
					variant="danger"
					class="width-full"
					on:click={() => {
						let udata = get(user_data);
						if (udata.refresh_token) delete udata.refresh_token;
						user_data.set(udata);
						console.log('Deleted Refresh Token');
					}}>Delete Refresh Token</Button
				>
			</Stack>

			<hr class="mt-l mb-l" />

			<Text class="mb-m text-bold">Auth token</Text>

			<Button
				variant="secondary"
				class="mb-m width-full"
				on:click={() => {
					user_data.set({
						...get(user_data),
						token: expiredTokenJJ,
					});
					customToken = expiredTokenJJ;
					console.log('Setting Expired JJ Token', expiredTokenJJ);
					handleToken(expiredTokenJJ);
				}}>Set Expired Token</Button
			>
			<Input
				class="mb-m"
				size="l"
				type="text"
				id="input-auth-token"
				label={'Auth token'}
				aria-labelledby="input-auth-token"
				value={customToken}
				on:click={(e) => {
					e.target.select();
				}}
				on:input={(e) => {
					customToken = e?.detail?.value;
				}}
			/>
			<Button
				on:click={() => {
					user_data.set({
						...get(user_data),
						token: customToken,
					});
					console.log('customToken', customToken);
					handleToken(customToken);
				}}>Set Auth Token</Button
			>

			<Button
				variant="secondary"
				class="ml-l"
				on:click={() => {
					customToken = get(user_data)?.token;
				}}>Get Current Token</Button
			>

			<hr class="mt-l mb-l" />

			<Text class="mb-m text-bold">Data loaders</Text>
			<Button
				class="width-full"
				on:click={() => {
					pullDataWithLoading();
				}}>Initial data load</Button
			>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Clear Installers home data</Text>
			<Button
				variant="primary"
				on:click={() => {
					delete_home('icodes');
					delete_home('installers_time');
				}}
			>
				Clear Installers home data</Button
			>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Stack gap="s" class="d-flex align-items-center mb-m">
				<Icon icon="fal fa-camera-web" size="m" />
				<Text styleType="h2" class="text-bold">Camera: Reset Cloud Recordings Setup</Text>
			</Stack>
			<Text styleType="body-small" class="mb-s">
				For the selected camera, this will permanently delete:
			</Text>
			<ul class="dev-info-list mb-m">
				{#if deleteAllRecordings}
					<li><Text styleType="body-small">Cloud Recordings for this camera</Text></li>
				{/if}
				<li><Text styleType="body-small">Encryption Key backed up in Cloud</Text></li>
				<li>
					<Text styleType="body-small"
						>Cached backup key, master key and password in this browser</Text
					>
				</li>
			</ul>

			{#if cameraOptions.length}
				<Select
					id="delete-cloud-setup-camera"
					class="width-md-xl width-sm-full mb-s"
					label="Camera"
					icon={null}
					options={cameraOptions}
					value={selectedCameraOption}
					aria-label="Select camera to delete Cloud Recordings setup for"
					on:change={handleCameraSelect}
				/>

				<Checkbox id="delete-cloud-setup-recordings" bind:checked={deleteAllRecordings}>
					Delete all Cloud Recordings for this device
				</Checkbox>

				{#if !deleteAllRecordings}
					<Text styleType="body-small" class="mt-s mb-s text-warning">
						Recordings will be kept, but every copy of their key is destroyed — they become
						permanently undecryptable.
					</Text>
				{/if}

				<Button
					class="mt-s"
					variant="danger"
					disabled={!selectedCameraMac}
					loading={deleteCloudRecordingsSetupLoading}
					on:click={() => (showDeleteCloudRecordingsSetupModal = true)}
					aria-label="Delete Cloud Recordings setup"
				>
					Delete Cloud Recordings Setup
				</Button>
			{:else}
				<Text styleType="body-small" class="text-warning">
					No camera devices on this account.
				</Text>
			{/if}
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset What's New Features</Text>
			<ResetWhatsNewFeatures />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m">Reset Houses Onboarding</Text>
			<ResetHomesOnboarding />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m">Reset App Icon</Text>
			<Text styleType="h2" class="mb-m text-bold">Reset App Icon</Text>
			<Button
				on:click={() => {
					save_home('appIconModal', 1680455849);
				}}>Reset App Icon</Button
			>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset Proactive Black Friday Modal</Text>
			<Button
				variant="primary"
				on:click={() => {
					save_profile('promotions', {
						blackFriday: {
							...$profile_data?.promotions?.blackFriday,
							proactive: 0,
						},
					});
				}}
			>
				Reset</Button
			>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset Ble Promo</Text>
			<ResetBlePromo />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset AI Chat Onboarding</Text>
			<Text class="mb-s"
				>След reset потребителят ще види onboarding модала при следващо отваряне на AI Assistant.</Text
			>
			<Stack gap="s" class="d-flex align-items-center">
				<Button variant="secondary" on:click={() => aiSceneChatStore.resetOnboarding()}>
					Reset Onboarding
				</Button>
				<Button variant="secondary" on:click={() => aiSceneChatStore.resetChatPopovers()}>
					Reset In-Modal Popovers
				</Button>
			</Stack>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset Promotions</Text>
			<Button
				class="mb-s"
				on:click={() =>
					save_profile('promotions', {
						...$profile_data?.promotions,
						annualSurvey: 0,
					})}>Reset Annual Survey</Button
			>
			<Button
				class="mb-s"
				on:click={() =>
					testDemoPromotionsModals.update((prevState) => ({
						...prevState,
						birthday: true,
					}))}>Show Birthday Offer</Button
			>
			<Button
				class="mb-s"
				on:click={() =>
					testDemoPromotionsModals.update((prevState) => ({
						...prevState,
						black_friday: true,
					}))}>Show Black Friday Offer</Button
			>
			<Button class="mb-s" on:click={() => save_profile('isShowedExpirationPremium', 'no')}
				>Reset Expiration Premium</Button
			>
			{#if $user_data.premium}
				<Button class="mb-s" on:click={() => save_profile('promoPremium', '1.1.1')}
					>Show Promo Premium</Button
				>
			{:else}
				<Button on:click={() => save_profile('promoBasic', '1.1.1')}>Show Promo Basic</Button>
			{/if}
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">ResetCampaigns</Text>
			<Button
				on:click={() => {
					if ($profile_data?.earthDaySceneCreated) {
						delete_profile('earthDaySceneCreated');
					}
					if ($profile_data?.aep?.ed) {
						const clonedAep = $profile_data?.aep;
						delete clonedAep.ed;
						profile_data.set({
							...$profile_data,
							aep: clonedAep,
						});
					}
					save_profile('promotions', {
						...$profile_data?.promotions,
						earthDay: 0,
					});
				}}>Earth Day</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.spinWheel) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							spinWheel: 0,
						});
					}
				}}>Spin Wheel</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.smartHomeChallenge) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							smartHomeChallenge: 0,
						});
					}
				}}>Smart Home Challenge</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.shellyBirthdayTeaser) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							shellyBirthdayTeaser: 0,
						});
					}
				}}>Shelly Birthday Teaser</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.shellyBirthdayCampaign) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							shellyBirthdayCampaign: 0,
						});
					}
				}}>Shelly Birthday Campaign</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.blackFridayPhase1) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							blackFridayPhase1: 0,
						});
					}
				}}>Black Friday Phase 1</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.blackFridayPhase2) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							blackFridayPhase2: 0,
						});
					}
				}}>Black Friday Phase 2</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.annualSurvey2026) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							annualSurvey2026: 0,
						});
					}
				}}>Annual Survey 2026</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.thirtyMillionDevices) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							thirtyMillionDevices: 0,
						});
					}
				}}>30 Million Devices</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.xlX2FlashDeal) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							xlX2FlashDeal: 0,
						});
					}
				}}>WallDisplay Flash Sale</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.wallDisplayFlashSale2) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							wallDisplayFlashSale2: 0,
						});
					}
				}}>Wall Display Flash Sale vol.2</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.springDeals2026) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							springDeals2026: 0,
						});
					}
				}}>Spring Deals 2026</Button
			>
			<Button
				class="mb-s"
				on:click={() => {
					if ($profile_data?.promotions?.smartHomeChallenge2026) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							smartHomeChallenge2026: 0,
						});
					}
				}}>Smart Home Challenge 2026</Button
			>
			<Button
				on:click={() => {
					if ($profile_data?.promotions?.shellyBirthday2026) {
						save_profile('promotions', {
							...$profile_data?.promotions,
							shellyBirthday2026: 0,
						});
					}
				}}>Shelly Birthday 2026</Button
			>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Runtime errors</Text>
			<Button variant="danger-secondary" on:click={() => testThrowError()}>Throw error</Button>
			<Button variant="danger" on:click={(e) => testRuntimeError(e)}>Critical Runtime error</Button
			>
			<Button variant="primary" on:click={(e) => testCaughtError(e)}>Caught error</Button>
			<Button variant="secondary" on:click={(e) => testConsoleError(e)}>Console error</Button>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset PP and GTC</Text>
			<ResetPPandGTC />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset Rate!</Text>
			<ResetRate />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Biometrics</Text>
			<Text styleType="body-small" class="mb-s">Ask for biometrics auth</Text>
			{#if $NativeApp}
				<Button on:click={() => sendBiometricsRequest()}>ASK ME!</Button>
				{#if $os !== 'IOS'}
					<Button on:click={() => askForSecurity()}>Go to security settings</Button>
				{/if}
				<Button on:click={() => clearPinUnlockedDevices()}>Clear Pin Unlocked Devices</Button>
				{#if biometricAuthResponse}
					<Text>Response: {JSON.stringify(biometricAuthResponse.biometry)}</Text>
					<Text>Error: {JSON.stringify(biometricAuthResponse.error) || ''}</Text>
				{/if}
			{:else}
				<Text styleType="body-small" class="mt-s mb-s text-warning"
					>Only available in Native App.</Text
				>
			{/if}
		</Container>
		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m">Send Only local commands</Text>
			<SendLocalCommandsUtility />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text class="mb-m">Get main route location / landing url</Text>
			<IconButton
				class="mb-s"
				on:click={() => getLandingHref()}
				icon="fas fa-clipboard"
				aria-label={$format('copy_to_clipboard', {
					default: 'Copy landing URL to clipboard',
				})}
			/>
			<Text class="mb-m">got to main route location</Text>
			<IconButton
				on:click={() => push(getLandingHref())}
				icon="fas fa-duck"
				aria-label={$format('navigate_to_main_route', {
					default: 'Navigate to main route',
				})}
			/>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text class="mb-m">Reset Alexa Integration Prompt</Text>
			<ResetAlexaIntegrationPrompt />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text class="mb-m">Reset Newsletter</Text>
			<ResetNewsletter />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text class="mb-m">Reset Premium EventLog Ads</Text>
			<ResetPremiumEventLogAds />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text class="mb-m">Reset Premium Scene Condition Ads</Text>
			<ResetPremiumSceneConditionAds />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text class="mb-m">Reset Premium Weather Widget Ads</Text>
			<ResetPremiumWeatherWidgetAds />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text class="mb-m">Test Confetti</Text>
			<TestConfetti bind:testConfetti />
		</Container>

		<hr class="mb-s mt-s" />

		{#if testConfetti}
			<Confetti
				delay={[0, 200]}
				amount={false ? 100 : 900}
				y={[-2, 2]}
				x={false ? [-1.5, 1.5] : [-5, 5]}
				fallDistance={'200px'}
			/>
		{/if}

		<Container padding="0">
			<Text class="mb-m">Test Global Temperature Units</Text>
			<ResetGlobalTempUnit />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<VerivoxDevSection />
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Reset Premium</Text>
			<Button
				variant="danger"
				on:click={() => {
					resetPremiumData();
				}}
			>
				Reset Premium
			</Button>
			<Text styleType="body-small" class="mb-s">Delete Voucher</Text>
			<Button
				variant="danger"
				on:click={() => {
					deleteVoucherFromAccount();
				}}
			>
				Delete Voucher
			</Button>
		</Container>

		<hr class="mb-s mt-s" />

		<Container padding="0">
			<Text styleType="h2" class="mb-m text-bold">Dashboard and Widgets</Text>

			<Text styleType="body" class="mb-s text-bold">All Devices table view</Text>
			<Text styleType="body-small" class="mb-m">
				Prototype dense table layout for All Devices / room lists. Visible on dev, staging, and
				other non-production builds only. Preference is saved locally.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$deviceTableViewEnabled ? 'primary' : 'secondary'}
					icon="fas fa-table"
					on:click={() => setDeviceTableViewEnabled(true)}
				>
					Enable table view
				</Button>
				<Button
					variant={!$deviceTableViewEnabled ? 'primary' : 'secondary'}
					icon="fas fa-eye-slash"
					on:click={() => setDeviceTableViewEnabled(false)}
				>
					Disable table view
				</Button>
			</Stack>
			<Text styleType="body-small" class="mb-l">
				Table view: <b>{$deviceTableViewEnabled ? 'Enabled' : 'Disabled'}</b>
			</Text>

			<Text styleType="body" class="mb-s text-bold">All Energy Widgets (testing)</Text>
			<Text styleType="body-small" class="mb-m">
				Adds or removes a virtual dashboard tab that renders every energy widget type.
				Client-only (<code>/dash/-104</code>), not persisted, and intended for visual QA.
				Preference is saved locally.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$allEnergyWidgetsGalleryEnabled ? 'primary' : 'secondary'}
					icon="fas fa-grid-2"
					on:click={() => setAllEnergyWidgetsGalleryEnabled(true)}
				>
					Add gallery tab
				</Button>
				<Button
					variant={!$allEnergyWidgetsGalleryEnabled ? 'primary' : 'secondary'}
					icon="fas fa-eye-slash"
					on:click={() => {
						setAllEnergyWidgetsGalleryEnabled(false);
						if (window.location.hash.includes('-104')) {
							push('/dash/energy_monitoring');
						}
					}}
				>
					Remove gallery tab
				</Button>
			</Stack>
			{#if $allEnergyWidgetsGalleryEnabled}
				<Button
					class="mb-m"
					variant="secondary"
					icon="fas fa-arrow-up-right-from-square"
					on:click={() => {
						openDevModal = false;
						push(`/dash${ALL_ENERGY_WIDGETS_DASHBOARD_ROUTE}`);
					}}
				>
					Open gallery
				</Button>
			{/if}
			<Text styleType="body-small" class="mb-l">
				Gallery tab: <b>{$allEnergyWidgetsGalleryEnabled ? 'Added' : 'Removed'}</b>
			</Text>

			<Text styleType="body" class="mb-s text-bold">All Device Widgets (testing)</Text>
			<Text styleType="body-small" class="mb-m">
				Adds or removes a virtual dashboard tab that renders every home device as two big widgets
				— Quick Control cards on the left and DashDeviceWidget cards on the right. Client-only (<code
					>/dash/-105</code
				>), not persisted, and intended for visual QA. Preference is saved locally.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$allDeviceWidgetsGalleryEnabled ? 'primary' : 'secondary'}
					icon="fas fa-toggle-on"
					on:click={() => setAllDeviceWidgetsGalleryEnabled(true)}
				>
					Add gallery tab
				</Button>
				<Button
					variant={!$allDeviceWidgetsGalleryEnabled ? 'primary' : 'secondary'}
					icon="fas fa-eye-slash"
					on:click={() => {
						setAllDeviceWidgetsGalleryEnabled(false);
						if (window.location.hash.includes('-105')) {
							push(`/dash/${DEFAULT_DASH_ID}`);
						}
					}}
				>
					Remove gallery tab
				</Button>
			</Stack>
			{#if $allDeviceWidgetsGalleryEnabled}
				<Button
					class="mb-m"
					variant="secondary"
					icon="fas fa-arrow-up-right-from-square"
					on:click={() => {
						openDevModal = false;
						push(`/dash${ALL_DEVICE_WIDGETS_DASHBOARD_ROUTE}`);
					}}
				>
					Open gallery
				</Button>
			{/if}
			<Text styleType="body-small" class="mb-l">
				Gallery tab: <b>{$allDeviceWidgetsGalleryEnabled ? 'Added' : 'Removed'}</b>
			</Text>

			<Text styleType="body" class="mb-s text-bold">Widget versions</Text>
			<Text styleType="body-small" class="mb-m">
				Shows a tiny version label in the corner of each energy dashboard widget (primary color).
				Preference is saved locally.
			</Text>
			<Stack gap="m" class="d-flex align-items-center mb-m">
				<Button
					variant={$energyWidgetVersionsEnabled ? 'primary' : 'secondary'}
					icon="fas fa-code"
					on:click={() => setEnergyWidgetVersionsEnabled(true)}
				>
					Show versions
				</Button>
				<Button
					variant={!$energyWidgetVersionsEnabled ? 'primary' : 'secondary'}
					icon="fas fa-eye-slash"
					on:click={() => setEnergyWidgetVersionsEnabled(false)}
				>
					Hide versions
				</Button>
			</Stack>
			<Text styleType="body-small" class="mt-s">
				Current: <b>{$energyWidgetVersionsEnabled ? 'On' : 'Off'}</b>
			</Text>
		</Container>
	</Stack>
</Modal>

<Modal
	showModal={showDeleteCloudRecordingsSetupModal}
	title={'Delete Cloud Recordings Setup?'}
	backdropClose={false}
	zIndex={1}
	on:close={() => (showDeleteCloudRecordingsSetupModal = false)}
>
	<svelte:fragment slot="content">
		<Text class="mb-s">
			This will permanently delete the following for <b>{selectedCameraTitle}</b>:
		</Text>
		<ul class="dev-info-list">
			{#if deleteAllRecordings}
				<li><Text styleType="body-small">All Cloud Recordings</Text></li>
			{/if}
			<li><Text styleType="body-small">Encryption Key backed up in Cloud</Text></li>
			<li>
				<Text styleType="body-small"
					>Cached backup key, master key and password in this browser</Text
				>
			</li>
		</ul>
		<Text styleType="body-small" class="mt-s text-warning">
			This cannot be undone. On mobile, the master key handed to native secure storage is only
			removed on logout.
		</Text>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="mr-s"
			variant="secondary"
			disabled={deleteCloudRecordingsSetupLoading}
			on:click={() => (showDeleteCloudRecordingsSetupModal = false)}
			aria-label="Cancel"
		>
			Cancel
		</Button>
		<Button
			variant="danger"
			loading={deleteCloudRecordingsSetupLoading}
			on:click={deleteCloudRecordingsSetup}
			aria-label="Confirm deleting Cloud Recordings setup"
		>
			Delete
		</Button>
	</div>
</Modal>

<style>
	.env-monitor-iframe {
		height: 400px;
		min-height: 360px;
		border: 0;
		display: block;
	}

	.dev-info-list {
		margin: 0;
		padding-left: 20px;
		list-style: disc;
	}

	.dev-info-list li + li {
		margin-top: 4px;
	}
</style>
